/**
 * DashboardTable - Student table for LiveDashboardV2
 * Shows all proctoring data with sorting and status actions
 */
import React from 'react'
import { Form, Button, Badge, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'
import { BsShieldLock } from 'react-icons/bs'
import { format } from 'date-fns'
import Countdown from 'react-countdown'
import moment from 'moment'
import EditableTimeCell from './EditableTimeCell'
import countdownRenderer from 'Components/Common/CountDownRenderer'
import styles from '../../LabsV2.module.css'

// Flag display configuration
const FLAG_DISPLAY = {
  'NONE': {
    icon: '✅',
    label: 'Active',
    color: 'success',
    description: 'Student is approved and active'
  },
  'IP_COLLISION': {
    icon: '⚠️',
    label: 'IP Collision',
    color: 'danger',
    description: 'Another student is already using this IP address'
  },
  'IP_CHANGED': {
    icon: '🔄',
    label: 'IP Changed',
    color: 'warning',
    description: 'Student changed machine but still within allowed IP range'
  },
  'IP_OUT_OF_RANGE': {
    icon: '🚫',
    label: 'Out of Range',
    color: 'danger',
    description: 'Student accessed from an IP OUTSIDE the allowed range'
  },
  'LATE_START': {
    icon: '⏰',
    label: 'Late Start',
    color: 'warning',
    description: 'Student joined after the start buffer expired'
  },
  'MANUAL_FLAG': {
    icon: '🚩',
    label: 'Flagged',
    color: 'danger',
    description: 'Manually flagged by instructor for suspicious activity'
  }
}

const DashboardTable = ({
  students,
  examId,
  clockOffset = 0,
  visibleColumns,
  sortField,
  sortOrder,
  onSort,
  selectedRows,
  onSelectRow,
  onSelectAll,
  allSelected,
  onApprove,
  onMarkAsLeft,
  onUndoMarkLeft,
  onManualFlag,
  onDataRefresh
}) => {
  // Check if column is visible
  const isColumnVisible = (columnId) => visibleColumns.includes(columnId)

  // Get sort icon for a column
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort className={styles.sortIcon} />
    }
    return sortOrder === 1
      ? <FaSortUp className={styles.sortIconActive} />
      : <FaSortDown className={styles.sortIconActive} />
  }

  // Handle column header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      if (sortOrder === 1) {
        onSort(field, -1)
      } else {
        onSort('', 0)
      }
    } else {
      onSort(field, 1)
    }
  }

  // Format start time
  const formatStartTime = (dateString) => {
    if (!dateString) return 'Not Started'
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm')
    } catch {
      return 'Invalid Date'
    }
  }

  // Format total extra time
  const formatExtraTime = (addedTime) => {
    const totalSeconds = addedTime || 0
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}m ${seconds}s`
  }

  // Render timer cell
  const renderTimer = (student) => {
    const serverNow = Date.now() - clockOffset

    if (student.end_time) {
      if (new Date(student.end_time).getTime() <= serverNow) {
        return '00:00:00:00'
      }
      return (
        <Countdown
          key={`end-${student.end_time}`}
          date={new Date(student.end_time).getTime()}
          now={() => Date.now() - clockOffset}
          renderer={countdownRenderer}
        />
      )
    }

    if (student.start_time && student.duration) {
      const computedEnd =
        new Date(student.start_time).getTime() +
        moment.duration(student.duration).asMilliseconds()
      if (computedEnd <= serverNow) {
        return '00:00:00:00'
      }
      return (
        <Countdown
          key={`computed-${computedEnd}`}
          date={computedEnd}
          now={() => Date.now() - clockOffset}
          renderer={countdownRenderer}
        />
      )
    }

    return '-'
  }

  // Format datetime for tooltip
  const formatDateTime = (dateString) => {
    if (!dateString) return ''
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss')
    } catch {
      return ''
    }
  }

  // Render status cell with flag reason
  const renderStatus = (student) => {
    // If student has left
    if (student.has_left) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <div className="text-start">
                <strong>Left Exam Hall</strong>
                <hr className="my-1" />
                <p className="mb-1">Marked as left by: {student.left_marked_by_name || 'Instructor'}</p>
                {student.left_at && (
                  <small>Left at: {formatDateTime(student.left_at)}</small>
                )}
              </div>
            </Tooltip>
          }
        >
          <Badge bg="secondary" style={{ cursor: 'pointer' }}>
            🚪 Left
          </Badge>
        </OverlayTrigger>
      )
    }

    // If approved, show active status (with flag history if previously flagged)
    if (student.is_approved) {
      if (student.flag_reason && student.flag_reason !== 'NONE') {
        const flagInfo = FLAG_DISPLAY[student.flag_reason] || FLAG_DISPLAY['NONE']
        return (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`resolved-flag-tooltip-${student.student_id}`}>
                <div className="text-start">
                  <strong>Previously flagged: {flagInfo.label}</strong>
                  <hr className="my-1" />
                  <p className="mb-1">{flagInfo.description}</p>
                  {student.flag_details && (
                    <p className="mb-1"><em>{student.flag_details}</em></p>
                  )}
                  {student.flagged_at && (
                    <small>Flagged: {formatDateTime(student.flagged_at)}</small>
                  )}
                </div>
              </Tooltip>
            }
          >
            <Badge bg="success" style={{ cursor: 'pointer' }}>
              ✅ Active <small>(was: {flagInfo.label})</small>
            </Badge>
          </OverlayTrigger>
        )
      }
      return (
        <Badge bg="success">✅ Active</Badge>
      )
    }

    // Show flag reason with tooltip
    const flagInfo = FLAG_DISPLAY[student.flag_reason] || FLAG_DISPLAY['NONE']

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`flag-tooltip-${student.student_id}`}>
            <div className="text-start">
              <strong>{flagInfo.label}</strong>
              <hr className="my-1" />
              <p className="mb-1">{flagInfo.description}</p>
              {student.flag_details && (
                <p className="mb-1"><em>{student.flag_details}</em></p>
              )}
              {student.flagged_at && (
                <small>Flagged: {formatDateTime(student.flagged_at)}</small>
              )}
            </div>
          </Tooltip>
        }
      >
        <Badge bg={flagInfo.color} style={{ cursor: 'pointer' }}>
          {flagInfo.icon} {flagInfo.label}
        </Badge>
      </OverlayTrigger>
    )
  }

  // Render "Mark as Left" column - always available if student has started
  const renderMarkAsLeft = (student) => {
    if (student.has_left) {
      return (
        <Button
          variant="outline-secondary"
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => onUndoMarkLeft(student.student_id)}
        >
          Undo Mark Left
        </Button>
      )
    }

    if (student.start_time) {
      return (
        <Button
          variant="outline-warning"
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => onMarkAsLeft(student.student_id)}
        >
          Mark as Left
        </Button>
      )
    }

    return null
  }

  // Render "Approve / Flag" column - conditional based on state
  const renderApproveFlag = (student) => {
    if (student.has_left) return null

    // If approved and started, show "Flag" button
    if (student.is_approved && student.start_time) {
      return (
        <Button
          variant="outline-danger"
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => onManualFlag(student.student_id)}
        >
          Flag
        </Button>
      )
    }

    // If not approved and no start_time - late start
    if (!student.start_time) {
      return (
        <DropdownButton
          variant="danger"
          title="Approve"
          size="sm"
          className={styles.statusDropdown}
        >
          <Dropdown.Item onClick={() => onApprove(student.student_id, 'remainingTime', 'accept_new')}>
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip>Approve with remaining time</Tooltip>}
            >
              <span>Remaining Time</span>
            </OverlayTrigger>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onApprove(student.student_id, 'fullTime', 'accept_new')}>
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip>Approve with full time</Tooltip>}
            >
              <span>Full Time</span>
            </OverlayTrigger>
          </Dropdown.Item>
        </DropdownButton>
      )
    }

    // IP not approved - check if there's an IP mismatch
    const hasIPMismatch = student.newipadd && student.newipadd !== student.ipaddress

    if (hasIPMismatch) {
      return (
        <DropdownButton
          variant="danger"
          title="Approve IP"
          size="sm"
          className={styles.statusDropdown}
        >
          <Dropdown.Item onClick={() => onApprove(student.student_id, 'fullTime', 'accept_new')}>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip>
                  Accept the new IP address ({student.newipadd}) -
                  student will continue from the new machine
                </Tooltip>
              }
            >
              <span>
                ✓ Accept New IP
                <br />
                <small className="text-muted">({student.newipadd})</small>
              </span>
            </OverlayTrigger>
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => onApprove(student.student_id, 'fullTime', 'keep_original')}>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip>
                  Keep the original IP address ({student.ipaddress}) -
                  reject access from the new machine
                </Tooltip>
              }
            >
              <span>
                ✗ Keep Original IP
                <br />
                <small className="text-muted">({student.ipaddress})</small>
              </span>
            </OverlayTrigger>
          </Dropdown.Item>
        </DropdownButton>
      )
    }

    // No IP mismatch but not approved (edge case)
    return (
      <Button
        variant="danger"
        size="sm"
        onClick={() => onApprove(student.student_id, 'fullTime', 'accept_new')}
        className={styles.statusBtnApproveIP}
      >
        Approve
      </Button>
    )
  }

  // Empty state
  if (students.length === 0) {
    return (
      <div className={styles.tableWrapper}>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <BsShieldLock size={32} />
          </div>
          <h3 className={styles.emptyStateTitle}>No student has started the exam</h3>
          <p className={styles.emptyStateText}>
            Students will appear here once they start the exam
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.labsTable}>
        <thead>
          <tr>
            <th style={{ width: '48px' }}>
              <Form.Check
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
            </th>
            <th style={{ width: '50px' }}>S.NO.</th>
            <th
              className={styles.sortableHeader}
              onClick={() => handleSort('student_email')}
              style={{ width: '180px' }}
            >
              EMAIL {getSortIcon('student_email')}
            </th>
            {isColumnVisible('name') && (
              <th
                className={styles.sortableHeader}
                onClick={() => handleSort('student_name')}
                style={{ width: '150px' }}
              >
                NAME {getSortIcon('student_name')}
              </th>
            )}
            {isColumnVisible('rollNo') && (
              <th
                className={styles.sortableHeader}
                onClick={() => handleSort('student_roll_no')}
                style={{ width: '120px' }}
              >
                ROLL NO {getSortIcon('student_roll_no')}
              </th>
            )}
            <th style={{ width: '140px' }}>IP ADDRESS</th>
            {isColumnVisible('newIP') && (
              <th style={{ width: '140px' }}>NEW IP ADDRESS</th>
            )}
            <th
              className={styles.sortableHeader}
              onClick={() => handleSort('start_time')}
              style={{ width: '160px' }}
            >
              START TIME {getSortIcon('start_time')}
            </th>
            <th
              className={styles.sortableHeader}
              onClick={() => handleSort('duration')}
              style={{ width: '120px' }}
            >
              TIMER {getSortIcon('duration')}
            </th>
            <th style={{ width: '180px' }}>ADD/REDUCE TIME</th>
            <th style={{ width: '140px' }}>TOTAL EXTRA TIME</th>
            <th
              className={styles.sortableHeader}
              onClick={() => handleSort('is_approved')}
              style={{ width: '140px' }}
            >
              STATUS {getSortIcon('is_approved')}
            </th>
            <th style={{ width: '140px' }}>MARK AS LEFT</th>
            <th style={{ width: '140px' }}>APPROVE / FLAG</th>
          </tr>
        </thead>
        <tbody>
          {students.map((student, index) => (
            <tr key={student.student_id}>
              <td>
                <Form.Check
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedRows.includes(student.student_id)}
                  onChange={() => onSelectRow(student.student_id)}
                />
              </td>
              <td>{index + 1}</td>
              <td>{student.student_email}</td>
              {isColumnVisible('name') && (
                <td>{student.student_name}</td>
              )}
              {isColumnVisible('rollNo') && (
                <td>{student.student_roll_no || '-'}</td>
              )}
              <td>
                {student.ipaddress || '-'}
                {student.newipadd && student.newipadd !== student.ipaddress && (
                  <div style={{ fontSize: '12px', color: '#dc3545' }}>
                    New: {student.newipadd}
                  </div>
                )}
              </td>
              {isColumnVisible('newIP') && (
                <td>{student.newipadd || '-'}</td>
              )}
              <td>{formatStartTime(student.start_time)}</td>
              <td>{renderTimer(student)}</td>
              <td>
                <EditableTimeCell
                  modifyAll={false}
                  studentId={student.student_id}
                  examId={examId}
                  onSuccess={onDataRefresh}
                />
              </td>
              <td>{formatExtraTime(student.added_time)}</td>
              <td>{renderStatus(student)}</td>
              <td>{renderMarkAsLeft(student)}</td>
              <td>{renderApproveFlag(student)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default DashboardTable
